import { ClassType, useAppSelector } from '@frontend/common';
import { IoTClient } from '@frontend/iot/api';
import { IoTStateName } from '@frontend/iot/types';
import { PackageClient } from '@frontend/package/api';
import { Package, PackageType } from '@frontend/package/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotType } from '@frontend/slot/types';
import { SpotClient } from '@frontend/spot/api';
import { Spot } from '@frontend/spot/types';
import { navigationStore, updateIoTState } from '@frontend/terminal/shared';
import { TransactionClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { TransactionStateName, TransactionType } from '@frontend/transaction/types';
import { UserClient } from '@frontend/user/api';
import { IUser } from '@frontend/user/types';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { UserInterfaceProps } from '../types/user-interface';

interface ViewProps {
    setSelectedUser: (user: IUser) => void;
    selectedUser: IUser | null;
    slotList: Slot[];
    changeSlot: Dispatch<SetStateAction<string | null>>;
    slot: string | null;
    isDropping: boolean;
    dropPackage: Package | null;
    onDropOff: () => void;
    slotMap: Map<string, ClassType>;
    disabledSlots: Slot[];
    spot: Spot | null;
}

const useDeliveryDropoff = (props: UserInterfaceProps): ViewProps => {
    const navigationState = useAppSelector(useSelector, navigationStore);
    const iot = navigationState.iot;
    const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
    const [sender, setSender] = useState<IUser | null>(null);
    const [isDropping, changeIsDropping] = useState<boolean>(false);
    const [dropPackage, changeDropPackage] = useState<Package | null>(null);
    const [slot, changeSlot] = useState<string | null>(null);
    const [slotList, changeSlotList] = useState<Slot[]>([]);
    const [foundSlot, changeFoundSlot] = useState<Slot | null>(null);
    const [availableSlots, changeAvailableSlots] = useState<Slot[]>([]);
    const [disabledSlots, changeDisabledSlots] = useState<Slot[]>([]);
    const transactionWorkflow = props.userInterface.data.transactionWorkflowId;
    const dropPackageWorkflow = props.userInterface.data.dropPackageWorkflowId;
    const pickPackageWorkflow = props.userInterface.data.pickPackageWorkflowId;
    const [spot, changeSpot] = useState<Spot | null>(null);

    const slotMap = useMemo(() => {
        const map = new Map<string, ClassType>();
        availableSlots.map((slot) => map.set(slot.id, ClassType.PRIMARY));

        return map;
    }, [availableSlots]);

    useEffect(() => {
        if (iot && iot.spot_id) {
            SpotClient.fetchSpot(iot.account_id, iot.spot_id).then(changeSpot);
        }
    }, [iot]);

    useEffect(() => {
        if (navigationState.user) {
            UserClient.fetchUser(navigationState.user.accountId, navigationState.user.id).then((res) => {
                setSender(res);
            });
        }
        if (iot) {
            IoTClient.patchIoT(iot.account_id, iot.id, { state: IoTStateName.IOT_DROP_OFF_PACKAGE }).then((iot) => {
                props.dispatch(updateIoTState({ iot_state: iot.state }));
            });
            const spotId = navigationState.iot?.spot_id;
            if (!spotId) return;
            SpotClient.fetchSpot(iot.account_id, spotId as string).then((spot) => {
                SlotClient.fetchSlots({ spot_id: spot.id, index: '0', size: '100' }).then((slots) => {
                    SlotWorkflowClient.postAvailableSlots(
                        spot.account_id,
                        spot.id,
                        {
                            account_id: iot.account_id,
                            transaction_type: TransactionType.OPEN_DROP_OFF
                        },
                        { index: '0', size: '100', type: SlotType.DEFAULT }
                    ).then((availableSlots) => {
                        changeSlotList(slots.results);
                        const disabledSlots = slots.results.filter((slot) => !availableSlots.results.map((s) => s.id).includes(slot.id));
                        changeDisabledSlots(disabledSlots);
                        changeAvailableSlots(availableSlots.results);
                    });
                });
            });
        }
    }, []);

    useEffect(() => {
        if (slot) {
            const foundSlot = slotList.find((s) => s.id === slot);
            if (!foundSlot) return;
            changeFoundSlot(foundSlot);
        }
    }, [slot]);

    const onDropOff = () => {
        if (sender && selectedUser && slot && iot) {
            if (!foundSlot) return;
            TransactionClient.postTransaction(iot.account_id, {
                type: TransactionType.OPEN_DROP_OFF,
                workflow_id: transactionWorkflow,
                iot_id: iot.id,
                state: TransactionStateName.TRANSACTION_CREATED
            }).then((transaction) => {
                const promisePackages = [];
                promisePackages.push(
                    PackageClient.postPackage(transaction.account_id, transaction.id, {
                        type: PackageType.DROP_OFF,
                        workflow_id: dropPackageWorkflow,
                        spot_id: foundSlot?.spot_id,
                        module_id: foundSlot?.module_id,
                        slot_id: slot,
                        priority: 1
                    }).then((res) => {
                        changeDropPackage(res);
                    })
                );
                promisePackages.push(
                    PackageClient.postPackage(transaction.account_id, transaction.id, {
                        type: PackageType.PICK_UP,
                        workflow_id: pickPackageWorkflow,
                        spot_id: foundSlot?.spot_id,
                        module_id: foundSlot?.module_id,
                        slot_id: slot,
                        priority: 0
                    })
                );

                Promise.all(promisePackages).then(() => {
                    TransactionWorkflowClient.updateTransactionState(transaction.account_id, transaction.id, TransactionStateName.TRANSACTION_PROCESSING);
                    changeIsDropping(true);
                });
            });
        }
    };

    return {
        setSelectedUser,
        selectedUser,
        changeSlot,
        slot,
        slotList,
        dropPackage,
        isDropping,
        onDropOff,
        disabledSlots,
        slotMap,
        spot
    };
};

export default useDeliveryDropoff;
